import Script from "next/script";
import { useMemo, useState } from "react";
import { displayFont } from "@/components/content-blocks/band-helpers";
import { pushDataLayerEvent } from "@/lib/analytics";

type ContactFormProps = {
  pagePath: string;
  pageEyebrow?: string;
  pageIntro?: string | null;
  pageTitle?: string;
  recaptchaSiteKey?: string | null;
};

type ContactFormValues = {
  name: string;
  email: string;
  subject: string;
  contactNumber: string;
  message: string;
};

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready(callback: () => void): void;
        execute(siteKey: string, options: { action: string }): Promise<string>;
      };
    };
  }
}

const initialValues: ContactFormValues = {
  name: "",
  email: "",
  subject: "",
  contactNumber: "",
  message: "",
};

function getErrorDetail(payload: unknown) {
  if (payload && typeof payload === "object") {
    const detail = (payload as { detail?: unknown }).detail;
    if (typeof detail === "string") {
      return detail;
    }
  }

  return null;
}

function requestCaptchaToken(siteKey: string) {
  return new Promise<string>((resolve, reject) => {
    const enterprise = window.grecaptcha?.enterprise;
    if (!enterprise) {
      reject(new Error("The verification service is still loading. Please try again."));
      return;
    }

    enterprise.ready(async () => {
      try {
        const token = await enterprise.execute(siteKey, {
          action: "contact_submit",
        });

        if (!token) {
          reject(new Error("Verification could not be completed. Please try again."));
          return;
        }

        resolve(token);
      } catch {
        reject(new Error("Verification could not be completed. Please try again."));
      }
    });
  });
}

export function ContactForm({
  pagePath,
  pageEyebrow = "Contact",
  pageIntro,
  pageTitle = "Get in touch",
  recaptchaSiteKey,
}: ContactFormProps) {
  const [values, setValues] = useState<ContactFormValues>(initialValues);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isCaptchaConfigured = useMemo(
    () => Boolean(recaptchaSiteKey?.trim()),
    [recaptchaSiteKey],
  );

  function updateValue<Key extends keyof ContactFormValues>(
    key: Key,
    value: ContactFormValues[Key],
  ) {
    setValues((current) => ({
      ...current,
      [key]: value,
    }));
  }

  const fieldLabelClassName =
    "storefront-page-overline px-1 text-[0.7rem] font-semibold uppercase tracking-[0.26em]";
  const textFieldClassName =
    "storefront-page-input min-h-14 w-full rounded-[1.25rem] px-5 py-4 text-base";
  const messageFieldClassName =
    "storefront-page-input min-h-[13.5rem] w-full rounded-[1.25rem] px-5 py-4 text-base leading-7 lg:min-h-[10.5rem]";
  const fieldWrapperClassName = "grid gap-2.5";

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!isCaptchaConfigured || !recaptchaSiteKey) {
      setErrorMessage(
        "The contact form is not fully configured right now. Please call or email the store instead.",
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const captchaToken = await requestCaptchaToken(recaptchaSiteKey);
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          ...values,
          captchaToken,
          pagePath,
        }),
      });

      const payload = (await response
        .json()
        .catch(() => null)) as { detail?: string } | null;

      if (!response.ok) {
        setErrorMessage(
          getErrorDetail(payload) ??
            "We could not send your message just now. Please try again shortly.",
        );
        return;
      }

      setValues(initialValues);
      setSuccessMessage(
        payload?.detail ??
          "Thanks for your message. We will get back to you as soon as we can.",
      );
      pushDataLayerEvent("generate_lead", { form_name: "contact" });
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "We could not send your message just now. Please try again shortly.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      {isCaptchaConfigured ? (
        <Script
          src={`https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(
            recaptchaSiteKey!,
          )}`}
          strategy="afterInteractive"
        />
      ) : null}

      <div className="space-y-6">
        <div className="storefront-page-divider space-y-3 border-b pb-6 sm:pb-7">
          <p className="storefront-page-overline text-[0.7rem] font-semibold uppercase tracking-[0.26em]">
            {pageEyebrow}
          </p>
          <h1 className={`${displayFont.className} storefront-page-title text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight`}>
            {pageTitle}
          </h1>
          {pageIntro ? (
            <p className="storefront-page-body max-w-2xl text-[0.98rem] leading-7 sm:text-[1.02rem]">
              {pageIntro}
            </p>
          ) : null}
        </div>

        {errorMessage ? (
          <div className="rounded-[1.35rem] border border-[#efd1d1] bg-[#fff4f4] px-4 py-3 text-sm leading-6 text-[#8d2f2f]">
            {errorMessage}
          </div>
        ) : null}

        {successMessage ? (
          <div className="storefront-page-success rounded-[1.8rem] px-6 py-7 sm:px-7 sm:py-8">
            <p className="storefront-page-overline text-[0.7rem] font-semibold uppercase tracking-[0.26em]">
              Message sent
            </p>
            <h2 className="storefront-page-title mt-3 text-[1.85rem] font-semibold tracking-tight sm:text-[2.2rem]">
              Thank you for getting in touch.
            </h2>
            <p className="storefront-page-body mt-4 max-w-2xl text-[1rem] leading-7">
              {successMessage}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-5 sm:grid-cols-2">
              <label className={fieldWrapperClassName}>
                <span className={fieldLabelClassName}>
                  Your Name
                </span>
                <input
                  type="text"
                  name="name"
                  value={values.name}
                  onChange={(event) => updateValue("name", event.target.value)}
                  autoComplete="name"
                  required
                  className={textFieldClassName}
                  placeholder="Enter your name"
                />
              </label>

              <label className={fieldWrapperClassName}>
                <span className={fieldLabelClassName}>
                  E-mail
                </span>
                <input
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={(event) => updateValue("email", event.target.value)}
                  autoComplete="email"
                  required
                  className={textFieldClassName}
                  placeholder="Enter your email"
                />
              </label>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <label className={fieldWrapperClassName}>
                <span className={fieldLabelClassName}>
                  Subject
                </span>
                <input
                  type="text"
                  name="subject"
                  value={values.subject}
                  onChange={(event) => updateValue("subject", event.target.value)}
                  autoComplete="off"
                  required
                  className={textFieldClassName}
                  placeholder="What can we help with?"
                />
              </label>

              <label className={fieldWrapperClassName}>
                <span className={fieldLabelClassName}>
                  Contact Number
                </span>
                <input
                  type="tel"
                  name="contactNumber"
                  value={values.contactNumber}
                  onChange={(event) =>
                    updateValue("contactNumber", event.target.value)
                  }
                  autoComplete="tel"
                  className={textFieldClassName}
                  placeholder="Optional"
                />
              </label>
            </div>

            <label className={fieldWrapperClassName}>
              <span className={fieldLabelClassName}>
                Message
              </span>
              <textarea
                name="message"
                value={values.message}
                onChange={(event) => updateValue("message", event.target.value)}
                rows={6}
                required
                className={messageFieldClassName}
                placeholder="Tell us about your enquiry"
              />
            </label>

            <div className="space-y-3 pt-1">
              <button
                type="submit"
                disabled={isSubmitting || !isCaptchaConfigured}
                className="storefront-page-button inline-flex min-h-14 items-center justify-center rounded-full px-8 text-base font-semibold"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
              <p className="storefront-page-body-muted max-w-2xl text-sm leading-6">
                This site is protected by reCAPTCHA and the Google{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="storefront-page-link font-medium underline decoration-current/35 underline-offset-3"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="storefront-page-link font-medium underline decoration-current/35 underline-offset-3"
                >
                  Terms of Service
                </a>{" "}
                apply.
              </p>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
